/**
 * Intent Classification Service
 * Handles AI-based intent classification for routing user requests
 *
 * Classifies user requests into:
 * - PAGE_GENERATION: Create new page/dashboard/layout
 * - COMPONENT_MODIFICATION: Add/modify/remove components
 * - PROPERTY_CHANGE: Change properties of existing components
 * - UNCLEAR: Cannot determine with confidence
 *
 * Performance: <500ms p95, >80% cache hit rate
 */

import { api } from '@/services/api/client';
import {
  IntentCategory,
  ClassificationContext,
  ClassificationResult,
  ClassificationAPIResponse
} from './types';
import * as errorUtils from '@/utils/errors';

export class IntentClassificationService {
  private static instance: IntentClassificationService;
  private localCache: Map<string, ClassificationResult>;
  private readonly maxCacheSize = 100; // Keep last 100 classifications cached

  private constructor() {
    this.localCache = new Map();
  }

  public static getInstance(): IntentClassificationService {
    if (!IntentClassificationService.instance) {
      IntentClassificationService.instance = new IntentClassificationService();
    }
    return IntentClassificationService.instance;
  }

  /**
   * Classify user intent to determine routing
   *
   * @param message - User's natural language request
   * @param context - Optional context about current state
   * @returns Promise<ClassificationResult> - Intent category with confidence
   *
   * @example
   * const result = await service.classifyIntent(
   *   "generate a dashboard that i can track soil moisture sensors",
   *   { isCanvasEmpty: true }
   * );
   * // result.intent === IntentCategory.PAGE_GENERATION
   */
  public async classifyIntent(
    message: string,
    context?: ClassificationContext
  ): Promise<ClassificationResult> {
    try {
      // Check local cache first (fastest)
      const cacheKey = this.getCacheKey(message, context);
      const cached = this.localCache.get(cacheKey);

      if (cached) {
        return { ...cached, cached: true };
      }

      const startTime = Date.now();

      // Call backend API with short timeout (classification should be fast)
      const response = await api.post<ClassificationAPIResponse>(
        '/ai/classify-intent',
        {
          message,
          context
        },
        {
          timeout: 5000 // 5 seconds max (target is <500ms but allow buffer)
        }
      );

      const latency = Date.now() - startTime;
      const classification = response.data;

      if (!response.success || !classification?.intent) {
        throw new Error(response.error || 'Classification failed');
      }

      const result: ClassificationResult = {
        intent: classification.intent,
        confidence: classification.confidence || 0.5,
        reasoning: classification.reasoning || 'No reasoning provided',
        cached: classification.cached || false,
        latency
      };

      // Cache the result locally
      this.addToLocalCache(cacheKey, result);

      return result;

    } catch (error: unknown) {
      const errorMessage = errorUtils.getErrorMessage(error);
      console.error('❌ [INTENT] Classification failed', {
        error: errorMessage,
        message: message.substring(0, 100)
      });

      // On error, return UNCLEAR intent with low confidence
      return {
        intent: IntentCategory.UNCLEAR,
        confidence: 0,
        reasoning: `Classification failed: ${errorMessage}`,
        cached: false,
        latency: 0
      };
    }
  }

  /**
   * Clear local cache
   */
  public clearCache(): void {
    this.localCache.clear();
  }

  /**
   * Get cache statistics
   */
  public getCacheStats() {
    return {
      size: this.localCache.size,
      maxSize: this.maxCacheSize
    };
  }

  /**
   * Generate cache key from message and context
   */
  private getCacheKey(message: string, context?: ClassificationContext): string {
    const normalized = message.toLowerCase().trim();
    const contextKey = context
      ? `${context.hasSelectedComponent}:${context.isCanvasEmpty}:${context.componentType || ''}`
      : 'no-context';

    return `${this.simpleHash(normalized)}:${contextKey}`;
  }

  /**
   * Simple string hash for cache keys
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Add to local cache with LRU eviction
   */
  private addToLocalCache(key: string, result: ClassificationResult): void {
    // Simple LRU: if cache is full, delete oldest entry
    if (this.localCache.size >= this.maxCacheSize) {
      const firstKey = this.localCache.keys().next().value;
      if (firstKey !== undefined) {
        this.localCache.delete(firstKey);
      }
    }

    this.localCache.set(key, result);
  }
}
